import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Bell,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  CheckCheck,
} from "lucide-react";

const CATEGORY_CFG = {
  success: { Icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  error: { Icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  warning: { Icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  request: { Icon: Bell, color: "text-[#FF5722]", bg: "bg-orange-50", border: "border-orange-200" },
  info: { Icon: Info, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
};

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

export default function Notifications() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () =>
    api.get("/notifications").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const onClick = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setItems((arr) =>
      arr.map((x) => (x.id === n.id ? { ...x, read: true } : x))
    );
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const filtered = items.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "high") return n.priority === "high";
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    const pa = PRIORITY_RANK[a.priority] ?? 1;
    const pb = PRIORITY_RANK[b.priority] ?? 1;
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const unread = items.filter((n) => !n.read).length;
  const high = items.filter((n) => n.priority === "high" && !n.read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-overline mb-1">{t("notifications")}</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">
            Bildirişlər
          </h1>
          <p className="text-slate-600 mt-2">
            {unread} oxunmamış · {high} yüksək prioritet
          </p>
        </div>
        {unread > 0 && (
          <Button
            variant="outline"
            onClick={markAll}
            data-testid="mark-all-page"
          >
            <CheckCheck className="w-4 h-4 mr-2" /> Hamısını oxu
          </Button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">
            Hamısı ({items.length})
          </TabsTrigger>
          <TabsTrigger value="unread" data-testid="tab-unread">
            Oxunmamış ({unread})
          </TabsTrigger>
          <TabsTrigger value="high" data-testid="tab-high">
            Yüksək ({items.filter((n) => n.priority === "high").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4">
          {sorted.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">{t("no_data")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map((n) => {
                const conf = CATEGORY_CFG[n.category] || CATEGORY_CFG.info;
                return (
                  <button
                    key={n.id}
                    onClick={() => onClick(n)}
                    className={`w-full text-left p-4 bg-white border rounded-lg hover:border-[#004D99] hover:shadow-sm transition-all flex items-start gap-4 ${
                      !n.read ? "border-l-4 border-l-[#004D99]" : "border-slate-200"
                    }`}
                    data-testid={`notif-row-${n.id}`}
                  >
                    <div className={`shrink-0 w-10 h-10 rounded-full ${conf.bg} flex items-center justify-center ${conf.color} border ${conf.border}`}>
                      <conf.Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-semibold ${!n.read ? "text-slate-900" : "text-slate-600"}`}>
                          {n.title || n.message}
                        </h3>
                        {n.priority && (
                          <span className={`text-[10px] mono px-1.5 py-0.5 rounded uppercase ${
                            n.priority === "high"
                              ? "bg-red-100 text-red-700"
                              : n.priority === "medium"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            {n.priority}
                          </span>
                        )}
                      </div>
                      {n.title && n.message !== n.title && (
                        <p className="text-sm text-slate-600 mb-2">{n.message}</p>
                      )}
                      <div className="mono text-[10px] text-slate-400">
                        {new Date(n.created_at).toLocaleString("az")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
